import { useState, useEffect } from 'react';
import { cariDB } from '../../../lib/cariDB';

type ClinicEntry = { clinic_name: string; contact_name?: string; phone?: string; id?: string };

type Props = {
  open: boolean;
  selectedClinics: string[];
  onClose: () => void;
  onChange: (selected: string[]) => void;
};

export default function ClinicSettingsPanel({ open, selectedClinics, onClose, onChange }: Props) {
  const [clinics, setClinics] = useState<ClinicEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [localSelected, setLocalSelected] = useState<string[]>(selectedClinics);

  useEffect(() => {
    if (open) {
      setLocalSelected(selectedClinics);
      loadClinics();
    }
  }, [open]);

  const loadClinics = async () => {
    setLoading(true);
    try {
      // getAllClinicsForSettings: hem cari_clinics hem cari_transactions'daki tüm klinikleri birleştirir
      const list = await cariDB.getAllClinicsForSettings();
      setClinics(list);
    } finally {
      setLoading(false);
    }
  };

  const toggle = (name: string) => {
    setLocalSelected((prev) =>
      prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name]
    );
  };

  const selectAll = () => setLocalSelected(clinics.map((c) => c.clinic_name));
  const clearAll = () => setLocalSelected([]);

  const handleSave = () => {
    onChange(localSelected);
    onClose();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-end">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/30 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Panel */}
      <div className="relative w-full max-w-sm h-screen bg-white flex flex-col shadow-2xl animate-slide-in-right">
        {/* Header */}
        <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between bg-gradient-to-r from-teal-600 to-cyan-600">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-white/20 rounded-lg flex items-center justify-center">
              <i className="ri-settings-3-line text-white text-lg"></i>
            </div>
            <div>
              <h2 className="text-base font-bold text-white">Tedavi Takip Ayarları</h2>
              <p className="text-xs text-teal-100">Takip edilecek klinikleri seçin</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/10 hover:bg-white/20 transition-all cursor-pointer"
          >
            <i className="ri-close-line text-white text-lg"></i>
          </button>
        </div>

        {/* Info */}
        <div className="px-6 py-4 bg-teal-50 border-b border-teal-100">
          <div className="flex items-start gap-2.5">
            <i className="ri-information-line text-teal-600 text-base mt-0.5 flex-shrink-0"></i>
            <p className="text-xs text-teal-700 leading-relaxed">
              Seçili kliniklerin hastalarına ait tedavi planları takip listesinde gösterilir.
              Klinik seçilmezse hiçbir hasta listelenmez.
            </p>
          </div>
        </div>

        {/* Toolbar */}
        <div className="px-6 py-3 border-b border-gray-100 flex items-center justify-between">
          <span className="text-sm font-semibold text-gray-700">
            Klinikler
            {clinics.length > 0 && (
              <span className="ml-2 text-xs font-normal text-gray-400">({clinics.length} klinik)</span>
            )}
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={selectAll}
              className="text-xs text-teal-600 hover:text-teal-700 font-medium cursor-pointer whitespace-nowrap"
            >
              Tümünü Seç
            </button>
            <span className="text-gray-300">|</span>
            <button
              onClick={clearAll}
              className="text-xs text-gray-500 hover:text-gray-700 font-medium cursor-pointer whitespace-nowrap"
            >
              Temizle
            </button>
          </div>
        </div>

        {/* Clinic list */}
        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-2 border-teal-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : clinics.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <i className="ri-hospital-line text-2xl text-gray-400"></i>
              </div>
              <p className="text-sm text-gray-500 font-medium">Kayıtlı klinik bulunamadı</p>
              <p className="text-xs text-gray-400 mt-1">Cari panelinden klinik ekleyin veya ameliyat kaydında klinik bilgisi girin.</p>
            </div>
          ) : (
            clinics.map((clinic) => {
              const isSelected = localSelected.includes(clinic.clinic_name);
              return (
                <button
                  key={clinic.clinic_name}
                  onClick={() => toggle(clinic.clinic_name)}
                  className={`w-full flex items-center gap-3 px-4 py-3.5 rounded-xl border transition-all cursor-pointer text-left ${
                    isSelected
                      ? 'bg-teal-50 border-teal-300'
                      : 'bg-white border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {/* Checkbox */}
                  <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                    isSelected ? 'bg-teal-500 border-teal-500' : 'border-gray-300'
                  }`}>
                    {isSelected && <i className="ri-check-line text-white text-xs"></i>}
                  </div>

                  {/* Clinic avatar */}
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 font-bold text-sm ${
                    isSelected ? 'bg-teal-500 text-white' : 'bg-gray-100 text-gray-500'
                  }`}>
                    {clinic.clinic_name.slice(0, 1).toUpperCase()}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-semibold truncate ${isSelected ? 'text-teal-700' : 'text-gray-800'}`}>
                      {clinic.clinic_name}
                    </p>
                    {clinic.contact_name && (
                      <p className="text-xs text-gray-500 truncate">{clinic.contact_name}</p>
                    )}
                  </div>
                </button>
              );
            })
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 bg-white">
          {localSelected.length > 0 && (
            <p className="text-xs text-teal-600 font-medium mb-3 flex items-center gap-1.5">
              <i className="ri-checkbox-circle-line text-sm"></i>
              {localSelected.length} klinik seçili — bu kliniklerin hastaları gösterilecek
            </p>
          )}
          {localSelected.length === 0 && clinics.length > 0 && (
            <p className="text-xs text-amber-600 mb-3 flex items-center gap-1.5">
              <i className="ri-alert-line text-sm"></i>
              Seçim yapılmadı — hiçbir hasta gösterilmeyecek
            </p>
          )}
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2.5 border border-gray-200 text-gray-600 rounded-lg text-sm font-medium hover:bg-gray-50 transition-all cursor-pointer whitespace-nowrap"
            >
              İptal
            </button>
            <button
              onClick={handleSave}
              className="flex-2 flex-grow px-5 py-2.5 bg-gradient-to-r from-teal-500 to-cyan-600 text-white rounded-lg text-sm font-semibold hover:from-teal-600 hover:to-cyan-700 transition-all cursor-pointer whitespace-nowrap"
            >
              <span className="flex items-center justify-center gap-1.5">
                <i className="ri-save-line"></i>Ayarları Kaydet
              </span>
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes slide-in-right {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        .animate-slide-in-right { animation: slide-in-right 0.25s ease-out; }
      `}</style>
    </div>
  );
}
